import { useEffect, useRef, useState } from 'react';

/** Rafraîchissement le plus fréquent d'un badge, hors navigation (ADR-092 §2). */
export const BADGE_REFRESH_MS = 60_000;

/**
 * Badges du menu : chaque requête nommée est exécutée par l'API, autorisée comme toute requête ;
 * un échec n'affiche simplement pas de badge. Relus à chaque navigation (`refreshKey`) et au plus
 * toutes les 60 secondes.
 */
export function useBadges(queries: readonly string[], load: ((query: string) => Promise<number | null>) | undefined, refreshKey: string): ReadonlyMap<string, number> {
  const [badges, setBadges] = useState<ReadonlyMap<string, number>>(new Map());
  const signature = queries.join('|');
  const loadRef = useRef(load);
  loadRef.current = load;
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (signature === '') return;
    const timer = setInterval(() => setTick((value) => value + 1), BADGE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [signature]);

  useEffect(() => {
    const loader = loadRef.current;
    if (signature === '' || loader === undefined) return;
    let live = true;
    void Promise.all(
      signature.split('|').map(async (query) => {
        try {
          return [query, await loader(query)] as const;
        } catch {
          return [query, null] as const;
        }
      }),
    ).then((entries) => {
      if (!live) return;
      const next = new Map<string, number>();
      for (const [query, value] of entries) if (typeof value === 'number' && Number.isFinite(value)) next.set(query, value);
      setBadges((previous) => (sameMap(previous, next) ? previous : next));
    });
    return () => {
      live = false;
    };
  }, [signature, refreshKey, tick]);

  return badges;
}

function sameMap(a: ReadonlyMap<string, number>, b: ReadonlyMap<string, number>): boolean {
  if (a.size !== b.size) return false;
  for (const [key, value] of a) if (b.get(key) !== value) return false;
  return true;
}

/** Total d'une réponse de requête : longueur d'un tableau, sinon un `total` numérique. */
export function totalOf(envelope: { data: unknown; meta?: Record<string, unknown> }): number | null {
  if (Array.isArray(envelope.data)) return envelope.data.length;
  const data = envelope.data as Record<string, unknown> | null;
  if (data !== null && typeof data === 'object' && typeof data['total'] === 'number') return data['total'];
  if (typeof envelope.meta?.['total'] === 'number') return envelope.meta['total'] as number;
  return null;
}
